import { supabase } from './supabase.js';

export const supabaseService = {
  // Family operations
  async createFamily(familyData, ownerId) {
    // Get the current user's email
    const { data: { user } } = await supabase.auth.getUser();
    const ownerEmail = user?.email;
    
    // In single-family architecture, just create the parent as family owner
    const { data: parentMember, error: parentError } = await supabase
      .from('family_members')
      .insert([{
        name: familyData.parentName,
        avatar: familyData.parentAvatar || '👩',
        role: 'parent',
        auth_user_id: ownerId,
        email: ownerEmail,
        is_owner: true
      }])
      .select()
      .single();
    
    if (parentError) throw parentError;

    // Create family data structure (single-family architecture)
    const familyStructure = {
      id: 'single-family', // Single family ID
      name: familyData.name || `${familyData.parentName}'s Family`,
      emoji: familyData.emoji || '🏠',
      parent_name: familyData.parentName,
      parent_email: ownerEmail,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    return {
      ...familyStructure,
      parentName: familyData.parentName,
      parentAvatar: familyData.parentAvatar || '👩',
      parentMemberId: parentMember.id,
      ownerId: ownerId,
      kids: [] // Kids will be added via invitations
    };
  },

  async getFamily() {
    // In single-family architecture, get family info from members
    const { data: members, error: membersError } = await supabase
      .from('family_members')
      .select('*')
      .order('is_owner', { ascending: false })
      .order('created_at', { ascending: true }); // Family owner first, then others by creation date
    
    if (membersError) throw membersError;
    
    // Separate parents and kids
    const parents = members.filter(m => m.role === 'parent');
    const kids = members.filter(m => m.role === 'child');
    
    // Create family data structure from members (single-family architecture)
    const primaryParent = parents.find(p => p.is_owner) || parents[0];
    const family = {
      id: 'single-family', // Single family ID
      name: primaryParent?.name ? `${primaryParent.name}'s Family` : 'Family',
      parent_name: primaryParent?.name || 'Parent',
      parent_email: primaryParent?.email || '',
      created_at: primaryParent?.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    // If no parents or kids found, return null
    if (parents.length === 0 && kids.length === 0) {
      console.log('No family members found');
      return null;
    }
    
    return {
      ...family,
      parentName: parents[0]?.name,
      parentAvatar: parents[0]?.avatar,
      parents: parents.map(parent => ({
        id: parent.id,
        name: parent.name,
        avatar: parent.avatar,
        email: parent.email,
        phone: parent.phone,
        role: parent.role,
        isOwner: parent.is_owner,
        authUserId: parent.auth_user_id,
        hasAccount: !!parent.auth_user_id
      })),
      kids: kids.map(kid => ({
        id: kid.id,
        name: kid.name,
        avatar: kid.avatar,
        age: kid.age,
        dateOfBirth: kid.date_of_birth,
        email: kid.email,
        phone: kid.phone,
        role: kid.role,
        authUserId: kid.auth_user_id,
        hasAccount: !!kid.auth_user_id
      }))
    };
  },

  async getFamilyByUserId(userId) {
    console.log('getFamilyByUserId called with:', userId);
    
    try {
      console.log('🔍 Fetching family for user with optimized query...');
      
      // First get the family member to verify user is part of family
      const { data: familyMember, error: memberError } = await supabase
        .from('family_members')
        .select('*')
        .eq('auth_user_id', userId)
        .single();
        
      if (memberError) {
        if (memberError.code === 'PGRST116') {
          console.log('No family member found for user');
          return null; // User not part of any family
        }
        console.error('Error fetching family member:', memberError);
        throw memberError;
      }
      
      if (!familyMember) {
        console.log('No family member found for user');
        return null;
      }
      
      // Get all family members in one query (single-family architecture)
      const { data: allMembers, error: membersError } = await supabase
        .from('family_members')
        .select('*')
        .order('is_owner', { ascending: false })
        .order('created_at', { ascending: true });
      
      if (membersError) {
        console.error('Error fetching family members:', membersError);
        throw membersError;
      }
      
      // Separate parents and kids
      const parents = allMembers.filter(m => m.role === 'parent');
      const kids = allMembers.filter(m => m.role === 'child');
      
      console.log('✅ Found family with', parents.length, 'parents and', kids.length, 'kids');
      
      // Create family data structure from family members (single-family architecture)
      const primaryParent = parents.find(p => p.is_owner) || parents[0];
      const familyData = {
        id: 'single-family', // Single family ID
        name: primaryParent?.name ? `${primaryParent.name}'s Family` : 'Family',
        parent_name: primaryParent?.name || 'Parent',
        parent_email: primaryParent?.email || '',
        created_at: primaryParent?.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      return {
        ...familyData,
        parentName: primaryParent?.name || familyData.parent_name,
        parentAvatar: primaryParent?.avatar,
        parents: parents.map(parent => ({
          id: parent.id,
          name: parent.name,
          avatar: parent.avatar,
          email: parent.email,
          phone: parent.phone,
          isOwner: parent.is_owner,
          authUserId: parent.auth_user_id,
          hasAccount: !!parent.auth_user_id
        })),
        kids: kids.map(kid => ({
          id: kid.id,
          name: kid.name,
          avatar: kid.avatar,
          age: kid.age,
          dateOfBirth: kid.date_of_birth,
          email: kid.email,
          phone: kid.phone,
          authUserId: kid.auth_user_id,
          hasAccount: !!kid.auth_user_id
        }))
      };
    } catch (error) {
      console.error('❌ Error in getFamilyByUserId:', error);
      throw error;
    }
  },

  // Activities operations
  async addActivity(userId, activity) {
    const { data, error } = await supabase
      .from('activities')
      .insert([{
        kid_id: activity.userType === 'kid' ? userId : null,
        parent_id: activity.userType === 'parent' ? userId : null,
        user_id: userId, // Always set for both parents and kids
        title: activity.name,
        category: activity.category,
        description: activity.description,
        energy_level: activity.energyLevel,
        how_to_start: activity.howToStart,
        links: activity.links || [],
        is_custom: activity.isCustom || false,
        added_by: activity.addedBy || 'user'
      }])
      .select()
      .single();
    
    if (error) throw error;
    return { 
      id: data.id,
      kidId: data.kid_id,
      parentId: data.parent_id,
      userId: data.user_id,
      name: data.title,
      title: data.title,
      category: this.mapDbCategoryToUi(data.category),
      description: data.description,
      energyLevel: data.energy_level,
      howToStart: data.how_to_start,
      links: data.links || [],
      isNew: true,
      isCustom: data.is_custom || false,
      addedBy: data.added_by || 'user',
      created_at: data.created_at,
      // Reconstruct userType based on whether it's for a parent or kid
      userType: data.parent_id ? 'parent' : 'kid'
    };
  },

  async getActivities() {
    const { data, error } = await supabase
      .from('activities')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    // Transform database fields to match UI expectations
    return data.map(activity => ({
      id: activity.id,
      kidId: activity.kid_id,
      parentId: activity.parent_id,
      userId: activity.user_id,
      name: activity.title,
      title: activity.title,
      category: this.mapDbCategoryToUi(activity.category),
      description: activity.description,
      energyLevel: activity.energy_level,
      howToStart: activity.how_to_start,
      links: activity.links || [],
      isNew: activity.is_new || false,
      isCustom: activity.is_custom || false,
      addedBy: activity.added_by || 'system',
      created_at: activity.created_at,
      // Reconstruct userType based on whether it's for a parent or kid
      userType: activity.parent_id ? 'parent' : 'kid'
    }));
  },

  async updateActivity(activityId, updates) {
    // Transform UI field names to database field names
    const dbUpdates = {};
    
    if (updates.name !== undefined) dbUpdates.title = updates.name;
    if (updates.title !== undefined) dbUpdates.title = updates.title;
    if (updates.category !== undefined) {
      // Map UI categories back to database categories
      const reverseMap = {
        'learning': 'educational',
        'lifeSkills': 'life_skills',
        'family': 'family_time',
        'professional': 'career'
      };
      dbUpdates.category = reverseMap[updates.category] || updates.category;
    }
    if (updates.description !== undefined) dbUpdates.description = updates.description;
    if (updates.energyLevel !== undefined) dbUpdates.energy_level = updates.energyLevel;
    if (updates.howToStart !== undefined) dbUpdates.how_to_start = updates.howToStart;
    if (updates.isNew !== undefined) dbUpdates.is_new = updates.isNew;
    if (updates.isCustom !== undefined) dbUpdates.is_custom = updates.isCustom;
    if (updates.addedBy !== undefined) dbUpdates.added_by = updates.addedBy;
    if (updates.links !== undefined) dbUpdates.links = updates.links;
    
    // Pass through any direct database field updates
    ['title', 'category', 'description', 'energy_level', 'how_to_start', 'is_new', 'is_custom', 'added_by', 'links'].forEach(field => {
      if (updates[field] !== undefined) dbUpdates[field] = updates[field];
    });

    const { data, error } = await supabase
      .from('activities')
      .update(dbUpdates)
      .eq('id', activityId)
      .select()
      .single();
    
    if (error) throw error;
    
    // Transform response back to UI format
    return {
      id: data.id,
      kidId: data.kid_id,
      parentId: data.parent_id,
      userId: data.user_id,
      name: data.title,
      title: data.title,
      category: this.mapDbCategoryToUi(data.category),
      description: data.description,
      energyLevel: data.energy_level,
      howToStart: data.how_to_start,
      links: data.links || [],
      isNew: data.is_new || false,
      isCustom: data.is_custom || false,
      addedBy: data.added_by || 'system',
      created_at: data.created_at
    };
  },

  // Helper method for category mapping
  mapDbCategoryToUi(dbCategory) {
    const categoryMap = {
      'educational': 'learning',
      'life_skills': 'lifeSkills',
      'family_time': 'family',
      'career': 'professional'
    };
    return categoryMap[dbCategory] || dbCategory;
  },

  async deleteActivity(activityId) {
    const { error } = await supabase
      .from('activities')
      .delete()
      .eq('id', activityId);
    
    if (error) throw error;
  },

  // Plans operations
  async savePlan(plan) {
    console.log('💾 savePlan called with:', { plan });
    
    const planData = {
      title: plan.title,
      date: plan.date,
      user_id: plan.user_id,
      user_type: plan.user_type || 'parent',
      description: plan.description || null,
      activities: plan.activities || null,
      big_3: plan.big_3 || null,
      kid_id: plan.kid_id || null
    };

    console.log('📝 Plan data to save:', planData);

    // Use upsert with the unique constraint (after running the SQL migration)
    const { data, error } = await supabase
      .from('plans')
      .upsert([planData], {
        onConflict: 'user_id,date'
      })
      .select()
      .single();
    
    if (error) {
      console.error('❌ Upsert error:', error);
      throw error;
    }
    
    console.log('✅ Plan saved successfully:', data);
    return data;
  },

  async getPlans() {
    const { data, error } = await supabase
      .from('plans')
      .select('*')
      .order('date', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  async getPlansInRange(userId, startDate, endDate) {
    const { data, error } = await supabase
      .from('plans')
      .select('*')
      .eq('user_id', userId)
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  // Patterns operations
  async savePattern(pattern) {
    const { data, error } = await supabase
      .from('patterns')
      .upsert([{
        name: pattern.name,
        activities: pattern.activities
      }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async getPatterns() {
    const { data, error } = await supabase
      .from('patterns')
      .select('*');
    
    if (error) throw error;
    return data;
  },

  // Family Events operations
  async addFamilyActivity(activity, createdBy) {
    console.log('📝 Adding family activity:', { activity, createdBy });
    try {
      // Ensure image is stored as a string URL
      const imageUrl = typeof activity.image === 'string' ? activity.image : (activity.image?.url || null);
      
      const { data, error } = await supabase
        .from('family_activities')
        .insert([{
          title: activity.title,
          description: activity.description,
          category: activity.category,
          location: activity.location || null,
          date: activity.date || null,
          start_date: activity.start_date || null,
          end_date: activity.end_date || null,
          time: activity.time || null,
          confirmed_for_calendar: activity.confirmed_for_calendar || false,
          color_theme: activity.color_theme || 'teal',
          type: activity.type,
          activities: activity.activities || [],
          meal_plan: activity.mealPlan,
          participants: activity.participants || [],
          estimated_duration: activity.estimatedDuration,
          min_participants: activity.minParticipants || 2,
          max_participants: activity.maxParticipants,
          suggested_ages: activity.suggestedAges,
          materials_needed: activity.materialsNeeded || [],
          cost_estimate: activity.costEstimate,
          difficulty_level: activity.difficultyLevel,
          weather_requirements: activity.weatherRequirements,
          best_time_of_day: activity.bestTimeOfDay,
          season_preference: activity.seasonPreference,
          notes: activity.notes,
          image: imageUrl,
          created_by: createdBy,
          status: activity.status || 'Unscheduled',
          votes: activity.votes || {},
          potential_dates: activity.potential_dates || [],
          // Trip-specific fields
          activity_type: activity.activity_type || 'activity',
          trip_metadata: activity.trip_metadata || null
        }])
        .select()
        .single();

      if (error) {
        console.error('❌ Error adding family activity:', error);
        throw error;
      }

      console.log('✅ Family activity added:', data);
      return { ...data, createdBy };
    } catch (error) {
      console.error('❌ Error in addFamilyActivity:', error);
      throw error;
    }
  },

  async getFamilyActivities() {
    console.log('📝 Fetching family activities');
    try {
      const { data, error } = await supabase
        .from('family_activities')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('❌ Error fetching family activities:', error);
        throw error;
      }

      // Only log in development for activities pages
      if (process.env.NODE_ENV === 'development' && window.location.pathname.includes('/activities')) {
        console.log('✅ Family activities fetched:', data?.length || 0);
      }
      
      if (!data || data.length === 0) {
        console.log('⚠️ No family activities found in database');
        return [];
      }
      
      const processedData = data.map(activity => {
        if (!activity) {
          console.warn('⚠️ Found null/undefined activity in data');
          return null;
        }
        
        const processed = {
          ...activity,
          votes: activity.votes || {},
          potential_dates: activity.potential_dates || [],
          status: activity.status || 'Unscheduled',
          category: activity.category || 'other'
        };
        
        // Removed verbose activity logging - was cluttering console on non-activity pages
        
        return processed;
      }).filter(Boolean); // Remove any null entries
      
      // Removed verbose logging - was cluttering console
      return processedData;
    } catch (error) {
      console.error('❌ Error in getFamilyActivities:', error);
      throw error;
    }
  },

  async updateFamilyActivity(activityId, updates) {
    console.log('📝 Updating family activity in Supabase:', { activityId, updates });
    try {
      // Clean up the updates to ensure empty strings become null
      // Only process fields that are actually being updated
      const cleanedUpdates = { ...updates };

      // Only process image if it's being updated
      if (updates.hasOwnProperty('image')) {
        cleanedUpdates.image = typeof updates.image === 'string' ? updates.image : (updates.image?.url || null);
      }
      
      // Handle trip-specific fields
      if (updates.hasOwnProperty('activity_type')) {
        cleanedUpdates.activity_type = updates.activity_type;
      }
      if (updates.hasOwnProperty('trip_metadata')) {
        cleanedUpdates.trip_metadata = updates.trip_metadata;
      }

      // Only process other fields if they're being updated
      if (updates.hasOwnProperty('date')) {
        cleanedUpdates.date = updates.date === '' ? null : updates.date;
      }
      if (updates.hasOwnProperty('start_date')) {
        cleanedUpdates.start_date = updates.start_date === '' ? null : updates.start_date;
      }
      if (updates.hasOwnProperty('end_date')) {
        cleanedUpdates.end_date = updates.end_date === '' ? null : updates.end_date;
      }
      if (updates.hasOwnProperty('time')) {
        cleanedUpdates.time = updates.time === '' ? null : updates.time;
      }
      if (updates.hasOwnProperty('location')) {
        cleanedUpdates.location = updates.location === '' ? null : updates.location;
      }

      // Never allow updating the creator when editing
      delete cleanedUpdates.created_by;

      const { data, error } = await supabase
        .from('family_activities')
        .update(cleanedUpdates)
        .eq('id', activityId)
        .select()
        .single();

      if (error) {
        console.error('❌ Error updating family activity:', error);
        throw error;
      }

      console.log('✅ Family activity updated:', data);
      return data;
    } catch (error) {
      console.error('❌ Error in updateFamilyActivity:', error);
      throw error;
    }
  },

  async deleteFamilyActivity(activityId) {
    console.log('🗑️ Deleting family activity:', activityId);
    try {
      const { error } = await supabase
        .from('family_activities')
        .delete()
        .eq('id', activityId);

      if (error) {
        console.error('❌ Error deleting family activity:', error);
        throw error;
      }

      console.log('✅ Family activity deleted');
    } catch (error) {
      console.error('❌ Error in deleteFamilyActivity:', error);
      throw error;
    }
  },

  // Mark activity as completed
  async markActivityCompleted(activityId, completionData) {
    console.log('✅ Marking activity as completed:', activityId, completionData);
    try {
      const { data, error } = await supabase
        .from('family_activities')
        .update({
          completed_date: completionData.completed_date,
          completed_by: completionData.completed_by || [],
          post_ratings: completionData.post_ratings || {},
          completion_notes: completionData.completion_notes || null,
          completion_photos: completionData.completion_photos || []
        })
        .eq('id', activityId)
        .select()
        .single();

      if (error) {
        console.error('❌ Error marking activity as completed:', error);
        throw error;
      }

      console.log('✅ Activity marked as completed:', data);
      return data;
    } catch (error) {
      console.error('❌ Error in markActivityCompleted:', error);
      throw error;
    }
  },

  // Get completed activities
  async getCompletedActivities() {
    console.log('📋 Fetching completed activities');
    try {
      const { data, error } = await supabase
        .from('family_activities')
        .select('*')
        .not('completed_date', 'is', null)
        .order('completed_date', { ascending: false });

      if (error) {
        console.error('❌ Error fetching completed activities:', error);
        throw error;
      }

      console.log(`✅ Fetched ${data?.length || 0} completed activities`);
      return data || [];
    } catch (error) {
      console.error('❌ Error in getCompletedActivities:', error);
      throw error;
    }
  },

  // Toggle activity favorite status
  async toggleActivityFavorite(activityId, isFavorite) {
    console.log(`⭐ Toggling favorite status for activity ${activityId} to ${isFavorite}`);
    try {
      const { data, error } = await supabase
        .from('family_activities')
        .update({ is_favorite: isFavorite })
        .eq('id', activityId)
        .select()
        .single();

      if (error) {
        console.error('❌ Error toggling activity favorite:', error);
        throw error;
      }

      console.log('✅ Activity favorite status updated:', data);
      return data;
    } catch (error) {
      console.error('❌ Error in toggleActivityFavorite:', error);
      throw error;
    }
  },

  // Calendar operations
  async addCalendarEvent(event) {
    const { data, error } = await supabase
      .from('calendar_events')
      .insert([{
        title: event.title,
        description: event.description,
        category: event.category,
        location: event.location,
        date: event.date,
        time: event.time,
        type: event.type,
        activities: event.activities || [],
        meal_plan: event.mealPlan,
        family_activity_id: event.familyActivityId,
        family_time_invite: event.family_time_invite,
        participants: event.participants || [],
        estimated_duration: event.estimatedDuration,
        min_participants: event.minParticipants || 2,
        max_participants: event.maxParticipants,
        suggested_ages: event.suggestedAges,
        materials_needed: event.materialsNeeded || [],
        cost_estimate: event.costEstimate,
        difficulty_level: event.difficultyLevel,
        weather_requirements: event.weatherRequirements,
        best_time_of_day: event.bestTimeOfDay,
        season_preference: event.seasonPreference,
        notes: event.notes,
        image: event.image?.url || event.image || null,
        status: event.status || 'scheduled',
        // New fields for creator and RSVP functionality
        created_by: event.created_by || event.createdBy,
        rsvp_enabled: event.rsvp_enabled || event.rsvpEnabled || false,
        rsvp_deadline: event.rsvp_deadline || event.rsvpDeadline,
        rsvps: event.rsvps || {}
      }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async getCalendarEvents() {
    const { data, error } = await supabase
      .from('calendar_events')
      .select('*')
      .order('date', { ascending: true });
    
    if (error) throw error;
    return data;
  },

  async updateCalendarEvent(calendarId, updates) {
    // Clean up the updates object
    const cleanedUpdates = {
      ...updates,
      // Ensure image is stored as a string URL
      image: updates.image?.url || updates.image || null
    };

    const { data, error } = await supabase
      .from('calendar_events')
      .update(cleanedUpdates)
      .eq('id', calendarId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async deleteCalendarEvent(eventId) {
    const { error } = await supabase
      .from('calendar_events')
      .delete()
      .eq('id', eventId);
    
    if (error) throw error;
  },

  // Ideas operations
  async addIdea(idea, suggestedBy) {
    const { data, error } = await supabase
      .from('ideas')
      .insert([{
        title: idea.title,
        description: idea.description,
        category: idea.category,
        target_kids: idea.targetKids || [],
        links: idea.links || [],
        created_by: suggestedBy,
        votes: {},
        // Enhanced fields
        steps: idea.steps || [],
        complexity: idea.complexity,
        link_metadata: idea.linkData,
        estimated_time: idea.estimatedTime,
        materials_needed: idea.materialsNeeded || [],
        prerequisites: idea.prerequisites || [],
        tags: idea.tags || [],
        enhanced_responses: {},
        image: idea.image,
        // Book-specific fields
        author: idea.author,
        genre: idea.genre,
        isbn: idea.isbn,
        is_audiobook: idea.isAudiobook,
        why_recommended: idea.whyRecommended,
        // Recipe-specific fields
        cuisine: idea.cuisine,
        recipe_type: idea.recipeType,
        // Class-specific fields
        is_virtual: idea.isVirtual,
        location: idea.location,
        hosted_by: idea.hostedBy,
        price: idea.price,
        start_date: idea.startDate,
        length: idea.length,
        additional_details: idea.additionalDetails,
        class_link: idea.classLink,
        // Project-specific fields
        core_problem: idea.coreProblem,
        real_world_context: idea.realWorldContext,
        skills_applied: idea.skillsApplied,
        pathways: idea.pathways,
        recommended_track: idea.recommendedTrack,
        portfolio_deliverables: idea.portfolioDeliverables,
        success_indicators: idea.successIndicators,
        resource_clusters: idea.resourceClusters,
        extension_challenges: idea.extensionChallenges,
        getting_started_steps: idea.gettingStartedSteps,
        // Movie/TV-specific fields
        rotten_tomatoes_critic: idea.rottenTomatoesCritic,
        rotten_tomatoes_audience: idea.rottenTomatoesAudience,
        google_audience_score: idea.googleAudienceScore,
        trailer_url: idea.trailerUrl,
        release_year: idea.releaseYear,
        director: idea.director,
        is_tv: idea.isTV
      }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async getIdeas(limit = 50, offset = 0) {
    const { data, error } = await supabase
      .from('ideas')
      .select('*')
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) throw error;
    if (!data || data.length === 0) return [];

    // Fetch all unique user IDs from ideas (author + target kids)
    const userIds = new Set();
    data.forEach(idea => {
      if (idea.created_by) userIds.add(idea.created_by);
      if (Array.isArray(idea.target_kids)) {
        idea.target_kids.forEach(kidId => userIds.add(kidId));
      }
    });

    // Create a map of users by ID
    const usersById = new Map();

    // Only fetch users if we have IDs
    if (userIds.size > 0) {
      const { data: users, error: usersError } = await supabase
        .from('family_members')
        .select('id, name, avatar')
        .in('id', Array.from(userIds));

      if (usersError) {
        console.error('Error fetching users for ideas:', usersError);
      } else {
        (users || []).forEach(user => {
          usersById.set(user.id, user);
        });
      }
    }

    // Transform snake_case to camelCase for frontend compatibility
    const transformedData = data.map(idea => ({
      ...idea,
      authorName: usersById.get(idea.created_by)?.name,
      authorAvatar: usersById.get(idea.created_by)?.avatar,
      targetKids: Array.isArray(idea.target_kids)
        ? idea.target_kids.map(kidId => usersById.get(kidId)).filter(Boolean)
        : [],
      createdBy: idea.created_by,
      kidResponses: idea.kid_responses || {},
      // Enhanced fields
      linkData: idea.link_metadata,
      estimatedTime: idea.estimated_time,
      materialsNeeded: idea.materials_needed || [],
      prerequisites: idea.prerequisites || [],
      enhancedResponses: idea.enhanced_responses || {},
      // Book-specific fields
      isAudiobook: idea.is_audiobook,
      whyRecommended: idea.why_recommended,
      // Recipe-specific fields
      recipeType: idea.recipe_type,
      // Class-specific fields
      isVirtual: idea.is_virtual,
      hostedBy: idea.hosted_by,
      startDate: idea.start_date,
      additionalDetails: idea.additional_details,
      classLink: idea.class_link,
      // Project-specific fields
      coreProblem: idea.core_problem,
      realWorldContext: idea.real_world_context,
      skillsApplied: idea.skills_applied,
      recommendedTrack: idea.recommended_track,
      portfolioDeliverables: idea.portfolio_deliverables,
      successIndicators: idea.success_indicators,
      resourceClusters: idea.resource_clusters,
      extensionChallenges: idea.extension_challenges,
      gettingStartedSteps: idea.getting_started_steps,
      // Movie-specific fields
      rottenTomatoesCritic: idea.rotten_tomatoes_critic,
      rottenTomatoesAudience: idea.rotten_tomatoes_audience,
      googleAudienceScore: idea.google_audience_score,
      trailerUrl: idea.trailer_url,
      releaseYear: idea.release_year,
      isTV: idea.is_tv
      // Note: author, genre, isbn, cuisine, director, location, price, length are already in correct format
    }));

    return transformedData;
  },

  async getIdeasCount() {
    const { count, error } = await supabase
      .from('ideas')
      .select('*', { count: 'exact', head: true });
    
    if (error) throw error;
    return count;
  },

  async updateIdea(ideaId, updates) {
    // Map form fields to database fields
    const dbUpdates = {};
    
    if (updates.title !== undefined) dbUpdates.title = updates.title;
    if (updates.description !== undefined) dbUpdates.description = updates.description;
    if (updates.category !== undefined) dbUpdates.category = updates.category;
    if (updates.targetKids !== undefined) dbUpdates.target_kids = updates.targetKids;
    if (updates.links !== undefined) dbUpdates.links = updates.links;
    if (updates.votes !== undefined) dbUpdates.votes = updates.votes;
    if (updates.is_favorite !== undefined) dbUpdates.is_favorite = updates.is_favorite;
    
    // Enhanced fields
    if (updates.steps !== undefined) dbUpdates.steps = updates.steps;
    if (updates.complexity !== undefined) dbUpdates.complexity = updates.complexity;
    if (updates.linkData !== undefined) dbUpdates.link_metadata = updates.linkData;
    if (updates.estimatedTime !== undefined) dbUpdates.estimated_time = updates.estimatedTime;
    if (updates.materialsNeeded !== undefined) dbUpdates.materials_needed = updates.materialsNeeded;
    if (updates.prerequisites !== undefined) dbUpdates.prerequisites = updates.prerequisites;
    if (updates.tags !== undefined) dbUpdates.tags = updates.tags;
    if (updates.enhancedResponses !== undefined) dbUpdates.enhanced_responses = updates.enhancedResponses;
    if (updates.image !== undefined) dbUpdates.image = updates.image;
    
    // Book-specific fields
    if (updates.author !== undefined) dbUpdates.author = updates.author;
    if (updates.genre !== undefined) dbUpdates.genre = updates.genre;
    if (updates.isbn !== undefined) dbUpdates.isbn = updates.isbn;
    if (updates.isAudiobook !== undefined) dbUpdates.is_audiobook = updates.isAudiobook;
    if (updates.whyRecommended !== undefined) dbUpdates.why_recommended = updates.whyRecommended;
    
    // Recipe-specific fields
    if (updates.cuisine !== undefined) dbUpdates.cuisine = updates.cuisine;
    if (updates.recipeType !== undefined) dbUpdates.recipe_type = updates.recipeType;
    
    // Class-specific fields
    if (updates.isVirtual !== undefined) dbUpdates.is_virtual = updates.isVirtual;
    if (updates.location !== undefined) dbUpdates.location = updates.location;
    if (updates.hostedBy !== undefined) dbUpdates.hosted_by = updates.hostedBy;
    if (updates.price !== undefined) dbUpdates.price = updates.price;
    if (updates.startDate !== undefined) dbUpdates.start_date = updates.startDate;
    if (updates.length !== undefined) dbUpdates.length = updates.length;
    if (updates.additionalDetails !== undefined) dbUpdates.additional_details = updates.additionalDetails;
    if (updates.classLink !== undefined) dbUpdates.class_link = updates.classLink;
    
    // Project-specific fields
    if (updates.coreProblem !== undefined) dbUpdates.core_problem = updates.coreProblem;
    if (updates.realWorldContext !== undefined) dbUpdates.real_world_context = updates.realWorldContext;
    if (updates.skillsApplied !== undefined) dbUpdates.skills_applied = updates.skillsApplied;
    if (updates.pathways !== undefined) dbUpdates.pathways = updates.pathways;
    if (updates.recommendedTrack !== undefined) dbUpdates.recommended_track = updates.recommendedTrack;
    if (updates.portfolioDeliverables !== undefined) dbUpdates.portfolio_deliverables = updates.portfolioDeliverables;
    if (updates.successIndicators !== undefined) dbUpdates.success_indicators = updates.successIndicators;
    if (updates.resourceClusters !== undefined) dbUpdates.resource_clusters = updates.resourceClusters;
    if (updates.extensionChallenges !== undefined) dbUpdates.extension_challenges = updates.extensionChallenges;
    if (updates.gettingStartedSteps !== undefined) dbUpdates.getting_started_steps = updates.gettingStartedSteps;
    
    // Movie/TV-specific fields
    if (updates.rottenTomatoesCritic !== undefined) dbUpdates.rotten_tomatoes_critic = updates.rottenTomatoesCritic;
    if (updates.rottenTomatoesAudience !== undefined) dbUpdates.rotten_tomatoes_audience = updates.rottenTomatoesAudience;
    if (updates.googleAudienceScore !== undefined) dbUpdates.google_audience_score = updates.googleAudienceScore;
    if (updates.trailerUrl !== undefined) dbUpdates.trailer_url = updates.trailerUrl;
    if (updates.releaseYear !== undefined) dbUpdates.release_year = updates.releaseYear;
    if (updates.director !== undefined) dbUpdates.director = updates.director;
    if (updates.isTV !== undefined) dbUpdates.is_tv = updates.isTV;
    
    // Pass through any direct database field updates
    ['target_kids', 'is_favorite', 'votes', 'enhanced_responses'].forEach(field => {
      if (updates[field] !== undefined) dbUpdates[field] = updates[field];
    });

    console.log('🔍 Updating idea with ID:', ideaId);
    console.log('🔍 Database updates:', dbUpdates);
    
    // First, check if the record exists and user has access
    const { data: existingRecord, error: checkError } = await supabase
      .from('ideas')
      .select('id, title, created_by')
      .eq('id', ideaId)
      .single();
    
    console.log('🔍 Existing record check:', existingRecord, 'error:', checkError);
    
    if (checkError) {
      console.error('❌ Cannot find or access record:', checkError);
      throw new Error(`Cannot find or access idea with ID ${ideaId}: ${checkError.message}`);
    }
    
    const { data, error } = await supabase
      .from('ideas')
      .update(dbUpdates)
      .eq('id', ideaId)
      .select();
    
    console.log('🔍 Update result - data:', data, 'error:', error);
    
    // Handle the response (data will be an array)
    const updatedRecord = data && data.length > 0 ? data[0] : null;
    
    if (error) {
      console.error('❌ Database update error:', error);
      throw error;
    }
    
    if (!data) {
      console.error('❌ No data returned - idea might not exist or no permission');
      throw new Error(`Idea with ID ${ideaId} not found or cannot be updated`);
    }
    
    return data;
  },

  async deleteIdea(ideaId) {
    // Full deletion: remove from all related tables

    // 1. Delete from entertainment_recommendations
    const { error: recsError } = await supabase
      .from('entertainment_recommendations')
      .delete()
      .eq('entertainment_id', ideaId);

    if (recsError) {
      console.error('Error deleting from entertainment_recommendations:', recsError);
      // Continue anyway
    }

    // 2. Delete all watch history entries
    const { error: watchHistoryError } = await supabase
      .from('family_watch_history')
      .delete()
      .eq('entertainment_id', ideaId);

    if (watchHistoryError) {
      console.error('Error deleting watch history:', watchHistoryError);
      // Continue anyway
    }

    // 3. Finally delete the movie/idea itself
    const { error } = await supabase
      .from('ideas')
      .delete()
      .eq('id', ideaId);

    if (error) throw error;

    console.log(`✅ Fully deleted idea ${ideaId} from ideas, entertainment_recommendations, and family_watch_history tables`);
  },

  // Movie deduplication helper
  async findOrCreateMovie(title, year, additionalData = {}) {
    try {
      const cleanTitle = title.trim();

      // Search for existing movie by title + year
      // Use ilike for case-insensitive search with the original title
      const { data: results, error: searchError } = await supabase
        .from('ideas')
        .select('*')
        .eq('category', 'movie')
        .ilike('title', cleanTitle)
        .eq('release_year', year);

      if (searchError) throw searchError;

      const existing = results && results.length > 0 ? results[0] : null;

      if (existing) {
        // MERGE: Preserve existing non-empty values, only update with new non-empty values
        const mergedData = { ...existing };

        Object.entries(additionalData).forEach(([key, value]) => {
          // Only overwrite if the new value is not empty and the existing value is empty
          // OR if we're explicitly updating certain fields (like votes, link_metadata)
          if (value !== undefined && value !== null && value !== '') {
            // For arrays and objects, always merge
            if (Array.isArray(value) || typeof value === 'object') {
              mergedData[key] = value;
            } else if (!existing[key] || existing[key] === '') {
              // For primitives, only update if existing is empty
              mergedData[key] = value;
            }
          }
        });

        // Update the existing movie
        const { data: updated, error: updateError } = await supabase
          .from('ideas')
          .update({
            ...mergedData,
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id)
          .select();

        if (updateError) throw updateError;

        return { ...(updated && updated.length > 0 ? updated[0] : mergedData), isUpdate: true };
      } else {
        // CREATE: Insert new movie
        const { data: created, error: createError } = await supabase
          .from('ideas')
          .insert([{
            title,
            release_year: year,
            category: 'movie',
            ...additionalData,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }])
          .select();

        if (createError) throw createError;

        return { ...(created && created.length > 0 ? created[0] : {}), isUpdate: false };
      }
    } catch (error) {
      console.error('❌ Error in findOrCreateMovie:', error);
      throw error;
    }
  },

  // Enhanced response handling
  async updateEnhancedResponse(ideaId, kidId, responseData) {
    // First get the current idea to merge responses
    const { data: currentIdea, error: fetchError } = await supabase
      .from('ideas')
      .select('enhanced_responses, votes')
      .eq('id', ideaId)
      .single();
    
    if (fetchError) throw fetchError;
    
    const currentResponses = currentIdea.enhanced_responses || {};
    const currentVotes = currentIdea.votes || {};
    
    // Create enhanced response object
    const enhancedResponse = {
      responseType: responseData.responseType,
      helpRequested: responseData.helpRequested || [],
      comment: responseData.comment || '',
      timing: responseData.timing || '',
      timestamp: new Date().toISOString(),
      viewed: false
    };
    
    // Update both enhanced_responses and legacy votes for compatibility
    const updatedResponses = {
      ...currentResponses,
      [kidId]: enhancedResponse
    };
    
    const updatedVotes = {
      ...currentVotes,
      [kidId]: responseData.responseType
    };
    
    const { data, error } = await supabase
      .from('ideas')
      .update({
        enhanced_responses: updatedResponses,
        votes: updatedVotes
      })
      .eq('id', ideaId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Get enhanced recommendations with response summary
  async getEnhancedRecommendations() {
    const { data, error } = await supabase
      .from('enhanced_recommendations_view')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  // Get kid response summary for parent dashboard
  async getKidResponsesSummary() {
    const { data, error } = await supabase
      .rpc('get_kid_responses_summary');
    
    if (error) throw error;
    return data;
  },

  // 1:1 Invites operations
  async addFamilyTimeInvite(invite, createdBy) {
    // Handle multiple participants by creating separate invites for each
    const participants = invite.participants || [];
    
    // Determine if this is a parent creating an invite for kids or vice versa
    const isParentCreating = participants.some(p => p !== 'parent');
    const isKidCreating = !isParentCreating;
    
    let invitee;
    if (isParentCreating) {
      // Parent is creating - invite kids (filter out 'parent')
      const invitees = participants.filter(p => p !== 'parent');
      if (invitees.length === 0) {
        throw new Error('At least one family member must be selected');
      }
      invitee = invitees[0]; // For now, handle single invitee
    } else {
      // Kid is creating - invite parents
      const parentInvitees = participants;
      if (parentInvitees.length === 0) {
        throw new Error('At least one parent must be selected');
      }
      invitee = parentInvitees[0]; // For now, handle single invitee
    }
    
    const { data, error } = await supabase
      .from('family_time_invites')
      .insert([{
        created_by: createdBy,
        invited_by: createdBy,
        invitee: invitee,
        activity_idea: invite.title || invite.activityIdea,
        preferred_time: invite.proposedDate || invite.preferredTime,
        message: invite.description || invite.message,
        location: invite.location,
        status: 'pending'
      }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async getFamilyTimeInvites() {
    const { data, error } = await supabase
      .from('family_time_invites')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    // Transform database fields to match what the UI expects
    return data.map(invite => {
      // Determine if this is a parent-to-kid or kid-to-parent invite
      // If created_by equals invitee, it means a kid is inviting a parent
      const isKidToParent = invite.created_by === invite.invitee ? false : true; // Actually, let's check participant structure
      
      return {
        id: invite.id,
        title: invite.activity_idea,
        description: invite.message,
        proposedDate: invite.preferred_time,
        location: invite.location,
        status: invite.status || 'pending',
        participants: invite.invitee ? [invite.created_by, invite.invitee] : [invite.created_by],
        kidResponse: invite.kid_response,
        responseDate: invite.response_date,
        respondingKid: invite.responding_kid,
        createdBy: invite.created_by,
        invitedBy: invite.invited_by,
        created_at: invite.created_at
      };
    });
  },

  async updateFamilyTimeInvite(inviteId, updates) {
    // Map form fields to database fields
    const dbUpdates = {};
    
    if (updates.title !== undefined) dbUpdates.activity_idea = updates.title;
    if (updates.description !== undefined) dbUpdates.message = updates.description;
    if (updates.proposedDate !== undefined) dbUpdates.preferred_time = updates.proposedDate;
    if (updates.location !== undefined) dbUpdates.location = updates.location;
    if (updates.status !== undefined) dbUpdates.status = updates.status;
    if (updates.kid_response !== undefined) dbUpdates.kid_response = updates.kid_response;
    if (updates.response_date !== undefined) dbUpdates.response_date = updates.response_date;
    if (updates.responding_kid !== undefined) dbUpdates.responding_kid = updates.responding_kid;
    
    // Handle participants -> invitee mapping for single invitee
    if (updates.participants) {
      const invitees = updates.participants.filter(p => p !== 'parent');
      if (invitees.length > 0) {
        dbUpdates.invitee = invitees[0];
      }
    }
    
    // Pass through any direct database field updates
    ['activity_idea', 'preferred_time', 'message', 'invitee'].forEach(field => {
      if (updates[field] !== undefined) dbUpdates[field] = updates[field];
    });
    
    const { data, error } = await supabase
      .from('family_time_invites')
      .update(dbUpdates)
      .eq('id', inviteId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async deleteFamilyTimeInvite(inviteId) {
    const { error } = await supabase
      .from('family_time_invites')
      .delete()
      .eq('id', inviteId);
    
    if (error) throw error;
  },

  // Allowance operations
  async getKidAllowance(kidId) {
    const { data, error } = await supabase
      .from('kid_allowances')
      .select('*')
      .eq('kid_id', kidId)
      .single();
    
    if (error && error.code !== 'PGRST116') throw error; // 116 is no rows found
    return data;
  },

  async createKidAllowance(kidId, weeklyAmount = 0, enabled = true) {
    const { data, error } = await supabase
      .from('kid_allowances')
      .insert([{
        kid_id: kidId,
        current_balance: 0,
        weekly_amount: weeklyAmount,
        enabled: enabled,
        last_weekly_addition: null
      }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateKidAllowance(kidId, updates) {
    const { data, error } = await supabase
      .from('kid_allowances')
      .update(updates)
      .eq('kid_id', kidId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async getAllowanceTransactions(kidId, limit = 50) {
    const { data, error } = await supabase
      .from('allowance_transactions')
      .select('*')
      .eq('kid_id', kidId)
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) throw error;
    return data;
  },

  async getFamilyAllowanceTransactions() {
    const { data, error } = await supabase
      .from('allowance_transactions')
      .select(`
        *,
        kid:family_members!kid_id(id, name)
      `)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    // Transform the data to have consistent field names
    return data.map(transaction => ({
      ...transaction,
      kidId: transaction.kid_id // Add kidId for backwards compatibility
    }));
  },

  async addAllowanceTransaction(kidId, amount, type, reason, approvedBy = null) {
    const { data, error } = await supabase
      .from('allowance_transactions')
      .insert([{
        kid_id: kidId,
        amount: amount,
        type: type, // 'addition', 'deduction', 'job_completion'
        reason: reason,
        approved_by: approvedBy
      }])
      .select()
      .single();
    
    if (error) throw error;

    // Update the kid's current balance
    if (type === 'addition' || type === 'job_completion') {
      await this.adjustKidBalance(kidId, amount);
    } else if (type === 'deduction') {
      await this.adjustKidBalance(kidId, -amount);
    }
    
    return data;
  },

  async adjustKidBalance(kidId, amountChange) {
    const { data, error } = await supabase.rpc('adjust_kid_balance', {
      p_kid_id: kidId,
      p_amount_change: amountChange
    });
    
    if (error) throw error;
    return data;
  },

  async recordSpendingRequest(kidId, amount, reason) {
    // Use the database function for immediate spending (no approval needed)
    const { data, error } = await supabase.rpc('record_kid_spending', {
      p_kid_id: kidId,
      p_amount: amount,
      p_reason: reason
    });
    
    if (error) throw error;
    return data;
  },

  async addWeeklyAllowance(kidId, amount, reason = 'Weekly allowance') {
    const { data, error } = await supabase
      .from('allowance_transactions')
      .insert([{
        kid_id: kidId,
        amount: amount,
        type: 'weekly_addition',
        reason: reason
      }])
      .select()
      .single();
    
    if (error) throw error;

    // Update balance and last weekly addition date
    await Promise.all([
      this.adjustKidBalance(kidId, amount),
      this.updateKidAllowance(kidId, { last_weekly_addition: new Date().toISOString() })
    ]);
    
    return data;
  },

  // Job operations
  async createJob(job, createdBy) {
    const { data, error } = await supabase
      .from('allowance_jobs')
      .insert([{
        title: job.title,
        description: job.description,
        amount: job.amount,
        created_by: createdBy,
        status: 'available',
        completion_deadline: job.completion_deadline
      }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async getAvailableJobs() {
    const { data, error } = await supabase
      .from('allowance_jobs')
      .select('*')
      .eq('status', 'available')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  async getAllJobs() {
    const { data, error } = await supabase
      .from('allowance_jobs')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  async acceptJob(jobId, kidId) {
    const { data, error } = await supabase
      .from('allowance_jobs')
      .update({
        status: 'in_progress',
        accepted_by: kidId,
        accepted_at: new Date().toISOString()
      })
      .eq('id', jobId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async completeJob(jobId, kidId) {
    const { data, error } = await supabase
      .from('allowance_jobs')
      .update({
        status: 'completed',
        completed_at: new Date().toISOString()
      })
      .eq('id', jobId)
      .eq('accepted_by', kidId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async approveJobCompletion(jobId, approvedBy) {
    // Get job details
    const { data: job, error: jobError } = await supabase
      .from('allowance_jobs')
      .select('*')
      .eq('id', jobId)
      .single();
    
    if (jobError) throw jobError;

    // Update job status
    const { data, error } = await supabase
      .from('allowance_jobs')
      .update({
        status: 'approved',
        approved_by: approvedBy,
        approved_at: new Date().toISOString()
      })
      .eq('id', jobId)
      .select()
      .single();
    
    if (error) throw error;

    // Add transaction and update balance
    await this.addAllowanceTransaction(
      job.accepted_by,
      job.amount,
      'job_completion',
      `Completed job: ${job.title}`,
      approvedBy
    );
    
    return data;
  },

  async rejectJobCompletion(jobId, reason = '') {
    const { data, error } = await supabase
      .from('allowance_jobs')
      .update({
        status: 'in_progress',
        rejection_reason: reason
      })
      .eq('id', jobId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Event linking operations
  async linkEvents(familyActivityId, calendarEventId) {
    // Start a transaction
    const { data: familyActivity, error: familyError } = await supabase
      .from('family_activities')
      .update({
        calendar_event_id: calendarEventId,
        updated_at: new Date().toISOString()
      })
      .eq('id', familyActivityId)
      .select()
      .single();

    if (familyError) throw familyError;

    const { data: calendarEvent, error: calendarError } = await supabase
      .from('calendar_events')
      .update({
        family_activity_id: familyActivityId,
        updated_at: new Date().toISOString()
      })
      .eq('id', calendarEventId)
      .select()
      .single();

    if (calendarError) throw calendarError;

    return { familyActivity, calendarEvent };
  },

  async updateEventParticipants(eventId, participants, eventType = 'family_activity') {
    const table = eventType === 'family_activity' ? 'family_activities' : 'calendar_events';
    const { data, error } = await supabase
      .from(table)
      .update({
        participants,
        updated_at: new Date().toISOString()
      })
      .eq('id', eventId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async getLinkedEvents(familyActivityId) {
    const { data: familyActivity, error: familyError } = await supabase
      .from('family_activities')
      .select('*, calendar_events(*)')
      .eq('id', familyActivityId)
      .single();

    if (familyError) throw familyError;
    return familyActivity;
  },

  async getCalendarEventWithFamilyActivity(calendarEventId) {
    const { data: calendarEvent, error: calendarError } = await supabase
      .from('calendar_events')
      .select('*, family_activities(*)')
      .eq('id', calendarEventId)
      .single();

    if (calendarError) throw calendarError;
    return calendarEvent;
  },

  // Image upload helper (single-family architecture)
  async uploadImage(file, familyId = 'single-family') {
    console.log('📤 Uploading image:', { fileName: file.name, fileSize: file.size, familyId });
    try {
      // Validate file
      if (!file || !file.type.startsWith('image/')) {
        throw new Error('Invalid file type. Please upload an image.');
      }

      // Check file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        throw new Error('File size too large. Maximum size is 10MB.');
      }

      const fileExt = file.name.split('.').pop().toLowerCase();
      const fileName = `single-family/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${fileName}`;

      console.log('📤 Attempting upload to path:', filePath);

      // First, check if bucket exists
      const { data: buckets, error: bucketError } = await supabase.storage.listBuckets();
      console.log('🪣 Available buckets:', buckets?.map(b => b.name));
      
      if (bucketError) {
        console.error('❌ Error listing buckets:', bucketError);
      }

      const { data, error: uploadError } = await supabase.storage
        .from('family-images')
        .upload(filePath, file, {
          contentType: file.type,
          upsert: false
        });

      if (uploadError) {
        console.error('❌ Error uploading image:', uploadError);
        
        // Check if it's a bucket not found error
        if (uploadError.message?.includes('not found') || uploadError.statusCode === 404) {
          throw new Error('Storage bucket not configured. Please contact support.');
        }
        
        throw uploadError;
      }

      console.log('📤 Upload response:', data);

      const { data: { publicUrl } } = supabase.storage
        .from('family-images')
        .getPublicUrl(filePath);

      console.log('✅ Image uploaded successfully:', publicUrl);
      return { url: publicUrl, path: filePath };
    } catch (error) {
      console.error('❌ Error in uploadImage:', error);
      // Return a more user-friendly error message
      if (error.message?.includes('Storage bucket not configured')) {
        throw error;
      }
      throw new Error(`Failed to upload image: ${error.message || 'Unknown error'}`);
    }
  },

  // Shopping List Integration Methods
  async addShoppingItemFromMessage(messageId, itemName, requesterId) {
    try {
      const { data, error } = await supabase.rpc('add_shopping_item_from_message', {
        p_message_id: messageId,
        p_item_name: itemName,
        p_quantity: 1,
        p_priority: 'normal',
        p_category_id: null,
        p_notes: null
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('❌ Error adding shopping item from message:', error);
      throw error;
    }
  },

  async getShoppingItems(filters = {}) {
    try {
      let query = supabase
        .from('shopping_items')
        .select(`
          *,
          category:shopping_categories(id, name, emoji, color),
          requester:family_members!requested_by(id, name, avatar),
          assignee:family_members!assigned_to(id, name, avatar),
          purchaser:family_members!purchased_by(id, name, avatar)
        `);

      if (filters.status) {
        query = query.eq('status', filters.status);
      }

      const { data, error } = await query
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('❌ Error getting shopping items:', error);
      throw error;
    }
  },

  async purchaseShoppingItem(itemId, purchaserId, price = null) {
    try {
      const { data, error } = await supabase.rpc('purchase_shopping_item', {
        p_item_id: itemId,
        p_purchaser_id: purchaserId,
        p_price: price
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('❌ Error purchasing shopping item:', error);
      throw error;
    }
  },

  // Family Messages Integration Methods
  async sendFamilyMessage(messageData, senderId) {
    try {
      const { data, error } = await supabase.rpc('send_family_message', {
        p_sender_id: senderId,
        p_type: messageData.type,
        p_subject: messageData.subject || null,
        p_content: messageData.content,
        p_recipient_ids: messageData.recipientIds || [],
        p_priority: messageData.priority || 'normal',
        p_expires_at: messageData.expiresAt || null,
        p_related_entity_type: messageData.relatedEntityType || null,
        p_related_entity_id: messageData.relatedEntityId || null
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('❌ Error sending family message:', error);
      throw error;
    }
  },

  async getFamilyMessages(filters = {}) {
    try {
      let query = supabase
        .from('family_messages')
        .select(`
          *,
          sender:family_members!sender_id(id, name, avatar, role),
          recipients:message_recipients(
            *,
            recipient:family_members!recipient_id(id, name, avatar, role)
          )
        `);

      if (filters.type) query = query.eq('type', filters.type);
      if (filters.status) query = query.eq('status', filters.status);
      if (filters.priority) query = query.eq('priority', filters.priority);

      const { data, error } = await query
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('❌ Error getting family messages:', error);
      throw error;
    }
  },

  async markMessageAsRead(messageId, readerId) {
    try {
      const { data, error } = await supabase.rpc('mark_message_read', {
        p_message_id: messageId,
        p_reader_id: readerId
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('❌ Error marking message as read:', error);
      throw error;
    }
  },

  async getUnreadMessageCount(userId) {
    try {
      const { count, error } = await supabase
        .from('message_recipients')
        .select('id', { count: 'exact' })
        .eq('recipient_id', userId)
        .eq('status', 'unread')
        .in('message_id', 
          supabase
            .from('family_messages')
            .select('id')
        );

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('❌ Error getting unread message count:', error);
      throw error;
    }
  },

  async createPersonalNote(ownerId, noteData) {
    try {
      const { data, error } = await supabase
        .from('personal_notes')
        .insert([{
          owner_id: ownerId,
          title: noteData.title,
          content: noteData.content,
          category: noteData.category,
          tags: noteData.tags || [],
          is_pinned: noteData.isPinned || false,
          related_entity_type: noteData.relatedEntityType,
          related_entity_id: noteData.relatedEntityId,
          reminder_date: noteData.reminderDate,
          reminder_time: noteData.reminderTime
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('❌ Error creating personal note:', error);
      throw error;
    }
  },

  async getPersonalNotes(ownerId, filters = {}) {
    try {
      let query = supabase
        .from('personal_notes')
        .select('*')
        .eq('owner_id', ownerId);

      if (filters.category) query = query.eq('category', filters.category);
      if (filters.isPinned) query = query.eq('is_pinned', filters.isPinned);

      const { data, error } = await query
        .order('is_pinned', { ascending: false })
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('❌ Error getting personal notes:', error);
      throw error;
    }
  },

  // ===== COLLEGE JOURNEY OPERATIONS =====

  // College management
  async getColleges(userId) {
    try {
      const { data, error } = await supabase
        .from('college_journey_colleges')
        .select(`
          *,
          college_journey_strategies (*)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('❌ Error getting colleges:', error);
      throw error;
    }
  },

  async addCollege(collegeData, userId) {
    try {
      const { data, error } = await supabase
        .from('college_journey_colleges')
        .insert([{
          ...collegeData,
          user_id: userId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('❌ Error adding college:', error);
      throw error;
    }
  },

  async updateCollege(collegeId, updates) {
    try {
      const { data, error } = await supabase
        .from('college_journey_colleges')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', collegeId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('❌ Error updating college:', error);
      throw error;
    }
  },

  async removeCollege(collegeId) {
    try {
      const { error } = await supabase
        .from('college_journey_colleges')
        .delete()
        .eq('id', collegeId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('❌ Error removing college:', error);
      throw error;
    }
  },

  // College strategy management
  async updateCollegeStrategy(collegeId, strategyData) {
    try {
      // First, try to get existing strategy
      const { data: existing, error: getError } = await supabase
        .from('college_journey_strategies')
        .select('*')
        .eq('college_id', collegeId)
        .single();

      if (getError && getError.code !== 'PGRST116') {
        throw getError;
      }

      if (existing) {
        // Update existing strategy
        const { data, error } = await supabase
          .from('college_journey_strategies')
          .update({
            ...strategyData,
            updated_at: new Date().toISOString()
          })
          .eq('college_id', collegeId)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Create new strategy
        const { data, error } = await supabase
          .from('college_journey_strategies')
          .insert([{
            college_id: collegeId,
            ...strategyData,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }])
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    } catch (error) {
      console.error('❌ Error updating college strategy:', error);
      throw error;
    }
  },

  // Essay management
  async getEssays(userId) {
    try {
      const { data, error } = await supabase
        .from('college_journey_essays')
        .select('*')
        .eq('user_id', userId)
        .eq('deleted', false)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('❌ Error getting essays:', error);
      throw error;
    }
  },

  async addEssay(essayData, userId) {
    try {
      const { data, error } = await supabase
        .from('college_journey_essays')
        .insert([{
          ...essayData,
          user_id: userId,
          created_at: new Date().toISOString(),
          last_modified: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('❌ Error adding essay:', error);
      throw error;
    }
  },

  async updateEssay(essayId, updates) {
    try {
      const { data, error } = await supabase
        .from('college_journey_essays')
        .update({
          ...updates,
          last_modified: new Date().toISOString()
        })
        .eq('id', essayId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('❌ Error updating essay:', error);
      throw error;
    }
  },

  async deleteEssay(essayId, permanent = false) {
    try {
      if (permanent) {
        const { error } = await supabase
          .from('college_journey_essays')
          .delete()
          .eq('id', essayId);

        if (error) throw error;
      } else {
        // Soft delete
        const { data, error } = await supabase
          .from('college_journey_essays')
          .update({
            deleted: true,
            deleted_at: new Date().toISOString()
          })
          .eq('id', essayId)
          .select()
          .single();

        if (error) throw error;
        return data;
      }
      return true;
    } catch (error) {
      console.error('❌ Error deleting essay:', error);
      throw error;
    }
  },

  // Timeline management
  async getTimeline(userId) {
    try {
      const { data, error } = await supabase
        .from('college_journey_timeline')
        .select('*')
        .eq('user_id', userId)
        .order('due_date', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('❌ Error getting timeline:', error);
      throw error;
    }
  },

  async addTimelineItem(timelineData, userId) {
    try {
      const { data, error } = await supabase
        .from('college_journey_timeline')
        .insert([{
          ...timelineData,
          user_id: userId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('❌ Error adding timeline item:', error);
      throw error;
    }
  },

  async updateTimelineItem(itemId, updates) {
    try {
      const { data, error } = await supabase
        .from('college_journey_timeline')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', itemId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('❌ Error updating timeline item:', error);
      throw error;
    }
  },

  // Master strategy management
  async getMasterStrategy(userId) {
    try {
      const { data, error } = await supabase
        .from('college_journey_master_strategy')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return data || {};
    } catch (error) {
      console.error('❌ Error getting master strategy:', error);
      throw error;
    }
  },

  async updateMasterStrategy(strategyData, userId) {
    try {
      // First, try to get existing strategy
      const { data: existing, error: getError } = await supabase
        .from('college_journey_master_strategy')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (getError && getError.code !== 'PGRST116') {
        throw getError;
      }

      if (existing) {
        // Update existing strategy
        const { data, error } = await supabase
          .from('college_journey_master_strategy')
          .update({
            ...strategyData,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Create new strategy
        const { data, error } = await supabase
          .from('college_journey_master_strategy')
          .insert([{
            user_id: userId,
            ...strategyData,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }])
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    } catch (error) {
      console.error('❌ Error updating master strategy:', error);
      throw error;
    }
  },

  // Free writes management
  async getFreeWrites(userId) {
    try {
      const { data, error } = await supabase
        .from('college_journey_freewrites')
        .select('*')
        .eq('user_id', userId)
        .order('date', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('❌ Error getting free writes:', error);
      throw error;
    }
  },

  async addOrUpdateFreeWrite(freeWriteData, userId) {
    try {
      // Try to get existing free write for this date
      const { data: existing, error: getError } = await supabase
        .from('college_journey_freewrites')
        .select('*')
        .eq('user_id', userId)
        .eq('date', freeWriteData.date)
        .single();

      if (getError && getError.code !== 'PGRST116') {
        throw getError;
      }

      if (existing) {
        // Update existing free write
        const { data, error } = await supabase
          .from('college_journey_freewrites')
          .update({
            content: freeWriteData.content,
            word_count: freeWriteData.word_count || 0,
            last_modified: new Date().toISOString()
          })
          .eq('id', existing.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Create new free write
        const { data, error } = await supabase
          .from('college_journey_freewrites')
          .insert([{
            user_id: userId,
            date: freeWriteData.date,
            content: freeWriteData.content,
            word_count: freeWriteData.word_count || 0,
            created_at: new Date().toISOString(),
            last_modified: new Date().toISOString()
          }])
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    } catch (error) {
      console.error('❌ Error adding/updating free write:', error);
      throw error;
    }
  }
};